import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClientAiProfile } from '../models/ai-profile.model';
import { DatabaseService } from './database.service';
import { FunctionsService } from './functions.service';

@Injectable({
  providedIn: 'root'
})
export class AiProfileService {
  private refreshingKey$ = new BehaviorSubject<string | null>(null);

  constructor(
    private dbService: DatabaseService,
    private functionsService: FunctionsService,
  ) {}

  getProfilePath(orgId: string, userId: string): string {
    return `aiProfiles/${orgId}/${userId}`;
  }

  private refreshKey(orgId: string, userId: string): string {
    return `${orgId}/${userId}`;
  }

  setRefreshing(orgId: string, userId: string, refreshing: boolean): void {
    const key = this.refreshKey(orgId, userId);
    if (refreshing) {
      this.refreshingKey$.next(key);
      return;
    }
    if (this.refreshingKey$.value === key) {
      this.refreshingKey$.next(null);
    }
  }

  isRefreshing(orgId: string, userId: string): Observable<boolean> {
    const key = this.refreshKey(orgId, userId);
    return this.refreshingKey$.pipe(map((active) => active === key));
  }

  getClientAiProfile(orgId: string, userId: string): Observable<ClientAiProfile | null> {
    return this.dbService.getListByRefTest<ClientAiProfile>(
      this.getProfilePath(orgId, userId)
    ).pipe(
      map((profile) => profile || null)
    );
  }

  async getClientAiProfileAsPromise(orgId: string, userId: string): Promise<ClientAiProfile | null> {
    const snap = await this.dbService.getSnapshot(this.getProfilePath(orgId, userId));
    return snap || null;
  }

  async regenerateClientAiProfile(orgId: string, userId: string): Promise<ClientAiProfile> {
    this.setRefreshing(orgId, userId, true);
    try {
      return await this.functionsService.callFunction("generateClientAiProfile", {
        orgId,
        userId,
      }) as Promise<ClientAiProfile>;
    } finally {
      this.setRefreshing(orgId, userId, false);
    }
  }
}
